import { CadModelCategory,  ModelSplitRequest, Part } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCategories, useSplitModel } from "@api/models";
import { useProject } from "@api/projects";
import NumberInput from "@components/NumberInput/NumberInput";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import { CategoryDto } from "@memorypack/CategoryDto";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGridPremium, GridCloseIcon, GridColDef } from "@mui/x-data-grid-premium";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useResizeObserver from "use-resize-observer";

export interface ProjectSplitDialogProps {
  open: boolean;
  onClose: () => void;
  data?: any;
}

interface PartRow {
  id: number;
  partName: string;
  categories: string[]; // TODO: rename this to cadModelCategoryIds
}

const ProjectSplitDialog = ({ open, onClose, data }: ProjectSplitDialogProps) => {
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const { id: projectId } = useParams();
  const [numParts, setNumParts] = useState<number | undefined>(undefined);
  const [parts, setParts] = useState<PartRow[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [updatedCadModelCategories, setUpdatedCadModelCategories] = useState<any>([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const categories = useCategories(data.modelId, undefined);
  const cadModelCategories = useCadModelCategories();
  const upStore = useUserPermissionStore();
  const project = useProject(projectId!);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    // TODO: test this with a random project guid
    selectionManager.setProject(project.data?.project);
    upStore.setResourcePermissions(project.data?.permissions);
  }, [project.data]);

  const splitModel = useSplitModel({
    onSuccess: () => {
      console.log("Model split created successfully.");
      onClose();
      project.refetch();
      setParts([]);
      setNumParts(undefined);
      setSelectedCategories([]);
    },
    onError: (error: any) => {
      console.error("Error creating model split:", error);
    },
  });

  const handleClose = () => {
    onClose();
    setNumParts(undefined);
    setParts([]);
    setSelectedCategories([]);
  };

  useEffect(() => {
    if (cadModelCategories.data && categories.data) {
      const updatedCategories = cadModelCategories.data
        ?.map((cadModelCategory: CadModelCategory) => {
          const matchingCategory = categories.data?.find((category: CategoryDto) => cadModelCategory.cadModelCategoryId === category.cadModelCategoryId);
          return matchingCategory ? { ...cadModelCategory, newField: matchingCategory } : null;
        })
        .filter(Boolean);

      if (updatedCategories) {
        updatedCategories.sort((a: any, b: any) => a.name.localeCompare(b.name));
      }
      setUpdatedCadModelCategories(updatedCategories);
    }
  }, [cadModelCategories.data, categories.data]);

  const getExistingSplitModels = () => {
    const baseModelName = data.modelName?.replace(/\.[^/.]+$/, "") + "_Part";
    return (
      selectionManager.selectedProject?.models?.filter(
        (model: any) => model.isSplitted === true && model.apsItemId === data.apsItemId && model.modelName?.startsWith(baseModelName),
      ) || []
    );
  };

  const extractPartNumberFromFileName = (fileName: string): number => {
    const matches = fileName.match(/_Part (\d+)/gi);
    if (matches && matches.length > 0) {
      const firstMatch = matches[0].match(/(\d+)/);
      return firstMatch ? parseInt(firstMatch[1], 10) : 0;
    }
    return 0;
  };

  const getMaxPartNumber = (splitModels: any[]): number => {
    const partNumbers = splitModels.map((model) => extractPartNumberFromFileName(model.apsItemName || "")).filter((num) => num > 0);

    return partNumbers.length > 0 ? Math.max(...partNumbers) : 0;
  };

  const generateNewParts = (count: number, startingPartNumber: number): PartRow[] => {
    return Array.from({ length: count }, (_, index) => {
      const existingPart = parts[index] || {
        id: index + 1,
        partName: `Part ${startingPartNumber + index}`,
        categories: [],
      };

      return {
        ...existingPart,
        id: index + 1,
        partName: existingPart.partName || `Part ${startingPartNumber + index}`,
      };
    });
  };

  const updateSelectedCategories = (newParts: PartRow[]) => {
    const allSelectedCategories = newParts.flatMap((part) => part.categories);
    setSelectedCategories([...new Set(allSelectedCategories)]);
  };

  useEffect(() => {
    if (numParts !== undefined) {
      const existingSplitModels = getExistingSplitModels();
      const maxPartNumber = getMaxPartNumber(existingSplitModels);
      const newParts = generateNewParts(numParts, maxPartNumber + 1);

      setParts(newParts);
      updateSelectedCategories(newParts);
    } else {
      setParts([]);
      setSelectedCategories([]);
    }
  }, [numParts]);

  const handleCategoryChange = (index: number) => (event: any, value: string[]) => {
    const newSelectedCategories = value;

    const updatedParts = [...parts];
    updatedParts[index].categories = newSelectedCategories;
    console.log("event", event);
    const allSelectedCategories = updatedParts.flatMap((part) => part.categories);
    setSelectedCategories([...new Set(allSelectedCategories)]);

    setParts(updatedParts);
    handlePartNameValidation(updatedParts);
  };

  const handleCreateSplit = () => {
    const partsPayload = parts.map(
      (part) =>
        new Part({
          partName: part.partName,
          cadModelCategoryIds: part.categories
            .map((categoryName) => {
              const category = updatedCadModelCategories.find((cat: { name: string }) => cat.name === categoryName);
              return category ? category.cadModelCategoryId : null;
            })
            .filter(Boolean),
        }),
    );

    const requestPayload = new ModelSplitRequest({
      modelId: data.modelId,
      projectId: data.projectId,
      parts: partsPayload,
    });

    splitModel.mutate(requestPayload);
  };

  const getAvailableCategories = () => {
    return updatedCadModelCategories.map((item: { name: any }) => item.name).filter((name: string) => !selectedCategories.includes(name));
  };

  const availableCategories = getAvailableCategories();

  const handleConfirmationClose = (proceed: any) => {
    setOpenConfirmationDialog(false);
    if (proceed) {
      handleCreateSplit();
    }
  };

  const handleCreateSplitClick = () => {
    if (availableCategories.length != 0) {
      setOpenConfirmationDialog(true);
    } else {
      handleCreateSplit();
    }
  };

  const validateDisplayName = (name: string) => {
    if (name.length > 255) {
      return i18n.t("model-split-dialog.validation.size");
    }

    const reservedChars = ["<", ">", ":", '"', "/", "\\", "|", "?", "*", "`", "\n", "\r", "\t", "\0", "\f", "$", "."];
    const invalidChars = reservedChars.filter((char) => name.includes(char));
    if (invalidChars.length > 0) {
      const formattedChars = invalidChars.map((char) => `'${char}'`).join(", ");
      return i18n.t("model-split-dialog.validation.invalid-characters", {
        chars: formattedChars,
      });
    }

    return "";
  };

  const validateUniquePartName = (newName: string, index: number) => {
    const fullModelName = data.apsItemName?.split(".")[0] + "_" + newName;
    const model = selectionManager.selectedProject?.models?.find((model: any) => model.apsItemName?.split(".")[0] === fullModelName) || null;
    const existingPart = parts.find((part, idx) => part.partName === newName && idx !== index);
    if (existingPart || model !== null) {
      return i18n.t("model-split-dialog.validation.unique-name");
    }
    return "";
  };

  const handlePartNameValidation = (newParts: PartRow[]) => {
    const hasInvalidPart = newParts.some((part) => {
      const validationError = validateDisplayName(part.partName);
      const validatepartname = validateUniquePartName(part.partName, newParts.indexOf(part));
      if (validationError || validatepartname) {
        return true;
      }
      return false;
    });
    setIsSubmitDisabled(hasInvalidPart);
  };

  const columns: GridColDef[] = [
    {
      field: "partName",
      headerName: i18n.t("model-split-dialog.column.parts"),
      flex: 0.5,
      renderCell: (params) => {
        const [error, setError] = useState("");

        const handlePartNameChange = (index: number, newName: string) => {
          const updatedParts = [...parts];
          updatedParts[index].partName = newName;
          setParts(updatedParts);
        };

        const handleChange = (event: any) => {
          const newName = event.target.value;

          const uniqueNameError = validateUniquePartName(newName, params.row.id - 1);
          if (uniqueNameError) {
            setError(uniqueNameError);
            return;
          }

          const validationError = validateDisplayName(newName);
          if (validationError) {
            setError(validationError);
          } else {
            setError("");
          }
          handlePartNameChange(params.row.id - 1, newName);
          handlePartNameValidation(parts);
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.code === "Space") {
            event.stopPropagation();
          }
        };

        return (
          <Tooltip title={error || ""} arrow open={!!error}>
            <TextField
              value={params.row.partName}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              sx={{
                width: "auto",
                pt: "10px",
                "& .MuiInputBase-input": { textAlign: "center" },
              }}
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "categories",
      headerName: i18n.t("model-split-dialog.column.cad-category"),
      flex: 2,
      renderCell: (params) => {
        return (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={availableCategories}
            value={params.row.categories}
            onChange={(event, newValue) => handleCategoryChange(params.row.id - 1)(event, newValue)}
            renderInput={(params) => <TextField {...params} variant="outlined" />}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={option} {...rest}>
                  {option}
                </li>
              );
            }}
            sx={{ width: "auto" }}
          />
        );
      },
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            width: "auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("model-split-dialog.title")}
          <IconButton aria-label="close" onClick={handleClose}>
            <GridCloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogTitle
          sx={{
            width: "auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("model-split-dialog.file-name", { modelname: data.apsItemName })}
        </DialogTitle>
        <DialogContent>
          <Grid container alignItems="center" mt={1} spacing={2}>
            <Grid>
              <Typography variant="body1" align="center" mr={2}>
                {i18n.t("model-split-dialog.number-of-parts")}
              </Typography>
            </Grid>
            <Grid>
              <NumberInput
                value={numParts}
                min={0}
                max={5}
                onChange={setNumParts}
                required
                sx={{ maxWidth: "150px", mb: 1 }}
                label={i18n.t("model-split-dialog.number-of-parts")}
              />
            </Grid>
          </Grid>
          <DataGridPremium columns={columns} getRowHeight={() => "auto"} rows={parts} disableColumnMenu disableRowSelectionOnClick sx={{ height: `${(height || 400) - 16}px` }} />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {i18n.t("non-model-element.add-non-model-element-dialog.cancel")}
          </Button>
          <Button
            autoFocus
            variant="contained"
            onClick={handleCreateSplitClick}
            loading={splitModel.isPending}
            disabled={isSubmitDisabled || parts.some((part) => part.categories.length === 0 || part.partName.trim().length === 0)}
          >
            {i18n.t("model-split-dialog.button.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmationDialog} onClose={() => setOpenConfirmationDialog(false)}>
        <DialogContent>
          <Typography variant="body1">{i18n.t("model-split-dialog.confirmation.message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmationClose(false)} variant="outlined">
            {i18n.t("model-split-dialog.confirmation.cancel")}
          </Button>
          <Button onClick={() => handleConfirmationClose(true)} variant="contained" color="primary">
            {i18n.t("model-split-dialog.confirmation.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectSplitDialog;
